import { useEffect, useMemo, useState } from "react";
import { Card } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { apiError, http, MONTH_LABELS } from "../lib/api";

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;
const shortMonth = (m) => MONTH_LABELS[Number(m.slice(5, 7)) - 1].slice(0, 3).toUpperCase();

const TONE = {
  pagado: "bg-emerald-100 text-emerald-900",
  parcial: "bg-amber-100 text-amber-900",
  pendiente: "bg-red-100 text-red-800",
  vacio: "bg-muted/40 text-ink-700",
};
const DOT = { pagado: "🟢", parcial: "🟠", pendiente: "🔴", vacio: "" };

const stateOf = (amount, paid) => {
  if (!amount) return "vacio";
  if (paid >= amount - 0.009) return "pagado";
  return paid > 0 ? "parcial" : "pendiente";
};

/** Celda editable: un clic marca pagado, dos clics permiten el importe exacto. */
const MoneyCell = ({ amount, paid, testid, onSave, label }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(paid ?? 0));
  const st = stateOf(amount, paid);

  if (!amount) {
    return <td className="p-1"><div data-testid={testid}
      className={`rounded-md px-2 py-2 text-center text-xs ${TONE.vacio}`}>—</div></td>;
  }
  return (
    <td className="p-1 align-top">
      {!open ? (
        <button type="button" data-testid={testid} title={label}
          onClick={() => { setValue(String(paid ?? 0)); setOpen(true); }}
          className={`w-full rounded-md px-2 py-2 text-center text-xs font-medium transition-opacity hover:opacity-80 ${TONE[st]}`}>
          {DOT[st]} {st === "pagado" ? eur(amount)
            : st === "parcial" ? `${eur(paid)}/${eur(amount)}`
              : `Pend. ${eur(amount - paid)}`}
        </button>
      ) : (
        <div className="min-w-[132px] rounded-md border-2 border-centro bg-white p-2">
          <p className="text-xs text-ink-700">{label} · {eur(amount)}</p>
          <div className="mt-1 flex gap-1">
            <Input data-testid={`${testid}-input`} value={value} inputMode="decimal"
              className="h-9 w-20 text-sm" autoFocus
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { onSave(Number(value.replace(",", "."))); setOpen(false); }
                if (e.key === "Escape") setOpen(false);
              }} />
            <Button data-testid={`${testid}-save`} className="h-9 px-2 text-xs"
              onClick={() => { onSave(Number(value.replace(",", "."))); setOpen(false); }}>
              OK
            </Button>
          </div>
          <div className="mt-1 flex gap-1">
            <Button data-testid={`${testid}-paid`} variant="outline" className="h-8 flex-1 px-1 text-xs"
              onClick={() => { onSave(amount); setOpen(false); }}>
              🟢 Pagado
            </Button>
            <Button data-testid={`${testid}-zero`} variant="outline" className="h-8 px-1 text-xs"
              onClick={() => { onSave(0); setOpen(false); }}>
              🔴 0 €
            </Button>
          </div>
        </div>
      )}
    </td>
  );
};

/** Cuadrante económico: todos los alumnos y meses en una sola pantalla, editable. */
export const FinanceSummary = () => {
  const [students, setStudents] = useState([]);
  const [patients, setPatients] = useState([]);
  const [months, setMonths] = useState([]);
  const [totals, setTotals] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyDebt, setOnlyDebt] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [s, p, f] = await Promise.all([
        http.get("/admin/students"), http.get("/admin/logopedia/patients"),
        http.get("/admin/finance-summary"),
      ]);
      setStudents(s.data);
      setPatients(p.data);
      setMonths(f.data.months);
      setTotals(f.data);
      setError("");
    } catch (err) { setError(apiError(err)); }
  };

  useEffect(() => { load(); }, []);

  const save = async (fn, okMsg) => {
    setMsg(""); setError("");
    try { await fn(); setMsg(okMsg); await load(); }
    catch (err) { setError(apiError(err)); }
  };

  const rows = useMemo(() => students
    .filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase()))
    .filter((s) => !onlyDebt || (s.total_debt || 0) > 0)
    .sort((a, b) => (b.total_debt || 0) - (a.total_debt || 0)), [students, search, onlyDebt]);

  const lrows = useMemo(() => patients
    .filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    .filter((p) => !onlyDebt || (p.total_debt || 0) > 0), [patients, search, onlyDebt]);

  const centroDebt = students.reduce((a, s) => a + (s.total_debt || 0), 0);
  const logoDebt = patients.reduce((a, p) => a + (p.total_debt || 0), 0);

  if (!totals) {
    return <Card><p className="text-lg text-ink-700">Cargando el cuadrante económico…</p></Card>;
  }

  return (
    <div className="space-y-6" data-testid="finance-summary">
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-xl bg-cream px-4 py-3" data-testid="finance-total">
          <span className="text-sm text-ink-700">A cobrar este mes</span>
          <strong className="ml-2 font-head text-xl">{eur(totals.total.amount)}</strong>
        </div>
        <div className="rounded-xl bg-emerald-100 px-4 py-3" data-testid="finance-paid">
          <span className="text-sm text-emerald-900">Cobrado</span>
          <strong className="ml-2 font-head text-xl text-emerald-900">{eur(totals.total.paid)}</strong>
        </div>
        <div className="rounded-xl bg-red-100 px-4 py-3" data-testid="finance-pending">
          <span className="text-sm text-red-800">Pendiente del mes</span>
          <strong className="ml-2 font-head text-xl text-red-800">{eur(totals.total.pending)}</strong>
        </div>
        <div className="rounded-xl bg-white px-4 py-3 ring-1 ring-border" data-testid="finance-debt">
          <span className="text-sm text-ink-700">Deuda acumulada</span>
          <strong className="ml-2 font-head text-xl">{eur(centroDebt + logoDebt)}</strong>
        </div>
        <Input data-testid="finance-search" value={search} placeholder="Buscar nombre"
          className="h-11 w-48 text-base" onChange={(e) => setSearch(e.target.value)} />
        <Button data-testid="finance-debt-filter" variant={onlyDebt ? "default" : "outline"}
          className="h-11 text-base" onClick={() => setOnlyDebt(!onlyDebt)}>
          {onlyDebt ? "Ver todos" : "Solo quien debe"}
        </Button>
      </div>

      {msg && <p data-testid="finance-success" className="rounded-xl bg-emerald-50 p-3 text-base text-emerald-900">{msg}</p>}
      {error && <p data-testid="finance-error" className="rounded-xl bg-red-50 p-3 text-base text-red-700">{error}</p>}

      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-head text-xl font-medium">Centro de Estudios · mensualidades del curso</h2>
          <p className="text-sm text-ink-700">
            Pulse una casilla para marcarla pagada o escribir el importe abonado · 🟢 pagado · 🟠 parcial · 🔴 pendiente
          </p>
        </div>
        <div className="mt-3 max-h-[70vh] overflow-auto" data-testid="centro-grid">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 min-w-[180px] border-b border-border bg-white p-2 text-left font-head text-base">
                  Alumno
                </th>
                {months.map((m) => (
                  <th key={m} className="sticky top-0 z-20 min-w-[112px] border-b border-border bg-white p-2 text-center text-xs font-semibold text-ink-700">
                    {shortMonth(m)}
                  </th>
                ))}
                <th className="sticky right-0 top-0 z-30 min-w-[120px] border-b border-border bg-white p-2 text-center text-xs font-semibold text-ink-700">
                  TOTAL PENDIENTE
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={months.length + 2} className="p-4 text-ink-700">Ningún alumno con ese nombre.</td></tr>
              )}
              {rows.map((s) => (
                <tr key={s.id} data-testid={`finance-row-${s.id}`} className="hover:bg-cream/50">
                  <th className="sticky left-0 z-10 border-b border-border bg-white p-2 text-left font-medium">
                    {s.name}
                  </th>
                  {months.map((m) => {
                    const f = (s.fees || []).find((x) => x.month === m) || { amount: 0, paid: 0 };
                    return (
                      <MoneyCell key={m} amount={f.amount} paid={f.paid}
                        label={`${s.name} · ${shortMonth(m)}`}
                        testid={`cell-${s.id}-${m}`}
                        onSave={(paid) => save(
                          () => http.put(`/admin/students/${s.id}/payments`, { month: m, paid }),
                          `${s.name}: ${shortMonth(m)} actualizado.`)} />
                    );
                  })}
                  <td className="sticky right-0 z-10 border-b border-border bg-white p-2 text-center"
                    data-testid={`finance-debt-${s.id}`}>
                    {s.total_debt > 0
                      ? <span className="rounded-md bg-red-100 px-2 py-1 font-semibold text-red-800">{eur(s.total_debt)}</span>
                      : <span className="text-ink-700">0 €</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-head text-xl font-medium">Logopedia · bonos</h2>
          <p className="text-sm text-ink-700">Pulse el importe abonado para corregirlo o marcarlo pagado</p>
        </div>
        <div className="mt-3 max-h-[60vh] overflow-auto" data-testid="logo-grid">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 min-w-[180px] border-b border-border bg-white p-2 text-left font-head text-base">Paciente</th>
                <th className="sticky top-0 z-20 border-b border-border bg-white p-2 text-left text-xs font-semibold text-ink-700">Sede</th>
                <th className="sticky top-0 z-20 border-b border-border bg-white p-2 text-left text-xs font-semibold text-ink-700">Bono</th>
                <th className="sticky top-0 z-20 border-b border-border bg-white p-2 text-left text-xs font-semibold text-ink-700">Sesiones</th>
                <th className="sticky top-0 z-20 border-b border-border bg-white p-2 text-center text-xs font-semibold text-ink-700">Importe / abonado</th>
                <th className="sticky top-0 z-20 border-b border-border bg-white p-2 text-center text-xs font-semibold text-ink-700">Bonos anteriores</th>
                <th className="sticky right-0 top-0 z-30 border-b border-border bg-white p-2 text-center text-xs font-semibold text-ink-700">TOTAL PENDIENTE</th>
              </tr>
            </thead>
            <tbody>
              {lrows.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-ink-700">Ningún paciente con ese nombre.</td></tr>
              )}
              {lrows.map((p) => {
                const active = (p.bonos || []).find((b) => b.active);
                const old = (p.bonos || []).filter((b) => !b.active && b.pending > 0);
                return (
                  <tr key={p.id} data-testid={`finance-lrow-${p.id}`} className="hover:bg-cream/50">
                    <th className="sticky left-0 z-10 border-b border-border bg-white p-2 text-left font-medium">{p.name}</th>
                    <td className="border-b border-border p-2">{p.sede === "xalo" ? "Xaló" : "Dénia"}</td>
                    <td className="border-b border-border p-2">Bono {p.bono_number} · {p.bono_total} ses.</td>
                    <td className="border-b border-border p-2">
                      {p.bono_used}/{p.bono_total} hechas · {p.bono_left} restantes
                    </td>
                    {active ? (
                      <MoneyCell amount={active.price} paid={active.paid}
                        label={`${p.name} · bono ${active.number}`}
                        testid={`lcell-${p.id}`}
                        onSave={(paid) => save(
                          () => http.put(`/admin/logopedia/bonos/${active.id}/payment`, { paid }),
                          `${p.name}: pago del bono actualizado.`)} />
                    ) : <td className="border-b border-border p-2 text-center text-ink-700">—</td>}
                    <td className="border-b border-border p-2 text-center">
                      {old.length === 0 ? <span className="text-ink-700">—</span> : (
                        <div className="space-y-1">
                          {old.map((b) => (
                            <button key={b.id} type="button" data-testid={`lold-${b.id}`}
                              onClick={() => save(
                                () => http.put(`/admin/logopedia/bonos/${b.id}/payment`, { paid: b.price }),
                                `Bono ${b.number} de ${p.name} marcado como pagado.`)}
                              className="w-full rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-900">
                              Bono {b.number}: debe {eur(b.pending)} · marcar pagado
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="sticky right-0 z-10 border-b border-border bg-white p-2 text-center"
                      data-testid={`finance-ldebt-${p.id}`}>
                      {p.total_debt > 0
                        ? <span className="rounded-md bg-red-100 px-2 py-1 font-semibold text-red-800">{eur(p.total_debt)}</span>
                        : <span className="text-ink-700">0 €</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
